import { themeClubbi, useMediaQuery } from 'clubbi-ui'
import { useRef, useState } from 'react'
import Countdown from 'react-countdown'
import { useHistory } from 'react-router-dom'

import { useAppSelector } from '../../../common/hooks'
import { handleScrollTo, showOrHideArrowsByScroll } from '../../../common/ScrollUtils'
import {
  anniversaryColor,
  getInfosBlackFriday,
  isClubbiAnniversary,
  pad,
} from '../../../common/utils'
import { Product } from '../../../store/data/types'
import { TagIcon } from '../categoriesHorizontalMenu/icons/TagIcon'
import { OfertaoCard } from './OfertaoCard'
import gtm from '../../../common/gtmTracker'

import {
  StyledArrowButton,
  StyledContainerOfertaoBox,
  StyledContentOfertaoBox,
  StyledCountdown,
  StyledText,
  StyledTextContent,
  StyledTitleDayOffers,
  StyledButton,
  StyledCardContainers,
  StyledContainer,
  StyledButtonTitle,
} from './style/OfertaoBox.style'

export interface OffersProps {
  products?: Product[]
}

export const getOfertaoTheme = () => {
  const blackFriday = getInfosBlackFriday()
  const anniversary = isClubbiAnniversary()

  if (blackFriday) {
    return {
      title: blackFriday.title,
      description: blackFriday.description,
      color: themeClubbi.palette.brandBlack.main,
      tagColor: themeClubbi.palette.brandWhite.main,
      variant: 'blackFriday' as const,
    }
  }

  if (anniversary) {
    return {
      title: 'Ofertas de aniversário!',
      description: 'Aproveite nossas ofertas especiais de aniversário 🥳',
      color: anniversaryColor.yellow,
      tagColor: anniversaryColor.deepPurple,
      variant: 'anniversary' as const,
    }
  }

  // default
  return {
    title: 'Ofertão do dia!',
    description: (
      <>
        Todos os dias <br />
        novas ofertas <br />
        pra você.
      </>
    ),
    color: themeClubbi.palette.brandYellow[40],
    tagColor: themeClubbi.palette.brandWhite.main,
    variant: 'default' as const,
  }
}

export const OfertaoBox = ({ products }: OffersProps) => {
  const [leftButton, setLeftButton] = useState(false)
  const [rightButton, setRightButton] = useState(true)

  const isDesktop = useMediaQuery(themeClubbi.breakpoints.up('md'))
  const { merchantCode, clubberEmail } = useAppSelector((state) => state.session)

  const corridor = useRef(null)
  const history = useHistory()

  const date = products && products?.[0]?.datetimeToExpire
  const offersQuantity = products?.length
  const seeMoreOffersButton = `VER ${offersQuantity} OFERTAS`

  const ofertaoTheme = getOfertaoTheme()

  const renderer = ({
    hours,
    minutes,
    seconds,
  }: {
    hours: number
    minutes: number
    seconds: number
    completed: boolean
  }) => {
    const isBlackFriday = ofertaoTheme.variant === 'blackFriday'
    const text = isBlackFriday ? 'Ofertas expiram em:' : 'Termina em:'

    const timer = `${text} ${pad(hours, 2)}:${pad(minutes, 2)}:${pad(seconds, 2)}`
    return <StyledCountdown variant={ofertaoTheme.variant}>{timer}</StyledCountdown>
  }

  const handleScrollBar = (_e: any) => {
    const element: any = corridor.current
    showOrHideArrowsByScroll(element, setRightButton, setLeftButton)
  }

  const handleScrollToRight = () => handleScrollTo(corridor.current, 'right')
  const handleScrollToLeft = () => handleScrollTo(corridor.current, 'left')

  const handleClickSeeMoreOffersButton = (): void => {
    gtm.triggerBannerOfertaoClick(merchantCode!, clubberEmail)
    history.push('/products/ofertoes')
  }

  return (
    <StyledContainerOfertaoBox>
      <StyledContentOfertaoBox variant={ofertaoTheme.variant}>
        <StyledTextContent variant={ofertaoTheme.variant}>
          <StyledContainer variant={ofertaoTheme.variant}>
            <StyledTitleDayOffers variant={ofertaoTheme.variant}>
              {ofertaoTheme.title}
            </StyledTitleDayOffers>
            {(isDesktop || ofertaoTheme.variant === 'blackFriday') && (
              <StyledText variant={ofertaoTheme.variant}>{ofertaoTheme.description}</StyledText>
            )}
            {!(ofertaoTheme.variant === 'blackFriday' && !isDesktop) && (
              <Countdown date={date} renderer={renderer} />
            )}
          </StyledContainer>

          {products && (
            <>
              <StyledButton variant={ofertaoTheme.variant} onClick={handleClickSeeMoreOffersButton}>
                {isDesktop && <TagIcon color={ofertaoTheme.tagColor} />}
                <StyledButtonTitle>{seeMoreOffersButton}</StyledButtonTitle>
              </StyledButton>
              {ofertaoTheme.variant === 'blackFriday' && !isDesktop && (
                <Countdown date={date} renderer={renderer} />
              )}
            </>
          )}
        </StyledTextContent>

        {offersQuantity! > 5 && leftButton && (
          <StyledArrowButton
            direction="left"
            onClick={handleScrollToRight}
            color={themeClubbi.palette.brandWhite.main}
          />
        )}

        <StyledCardContainers onScroll={handleScrollBar} ref={corridor}>
          {products?.map((product, index) => (
            <OfertaoCard product={product} key={index} />
          ))}
        </StyledCardContainers>

        {offersQuantity! > 5 && rightButton && (
          <StyledArrowButton
            direction="right"
            onClick={handleScrollToLeft}
            color={themeClubbi.palette.brandWhite.main}
          />
        )}
      </StyledContentOfertaoBox>
    </StyledContainerOfertaoBox>
  )
}
